import React from 'react';

import {
    setWidgetLoader,
    setGaugeWidgetConfiguration,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';

import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { useGetLoadGroupsListAllQuery } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { setLoadGroupsData } from '@fiji/common/src/features/widgetManagement/loadGroupWidgetSlice';
import { setLoadGroupTrendsData } from '@fiji/common/src/features/widgetManagement/loadGroupTrendsSlice';

export const useHandleLoadGroupsWidget = (widgetId?: string): any => {
    const dispatch = useAppDispatch();
    const device = useTypedSelector((state) => state['widgetConfiguration']?.device);
    const multiDevice = useTypedSelector((state) => state['widgetConfiguration']['data']['multiDevice']);
    const isAutomated = useTypedSelector((state) => state[widgetId ?? 'load_groups']['isAutomated']);
    const loadsPayload = useTypedSelector((state) => state[widgetId ?? 'load_groups']);

    // Tracks whether we have done the one-time reconciliation on initial load.
    // After this runs once, user check/uncheck interactions are never overridden.
    const hasReconciledRef = React.useRef(false);

    const {
        data: loadGroupLists,
        isError: multiLoadsError,
        isLoading: multiLoader,
        isFetching: multiFetcher,
    }: any = useGetLoadGroupsListAllQuery(
        {
            page: 0,
            size: isAutomated || loadsPayload?.secondary?.length ? 25 : 0,
            sort: { key: 'name', sortType: 'ASC' },
            ...(device?.id && { deviceId: [device?.id] }),
            ...(!isAutomated && {
                ids: loadsPayload?.secondary?.length ? loadsPayload?.secondary?.map((item: any) => item.id) : [],
            }),
        },

        {
            skip: multiDevice,
            refetchOnMountOrArgChange: true,
        }
    );

    React.useEffect(() => {
        if (multiLoader || multiFetcher) {
            dispatch(setWidgetLoader(true));
        }
    }, [multiLoader, multiFetcher]);

    React.useEffect(() => {
        if (multiLoadsError) {
            dispatch(setWidgetLoader(false));
        }
    }, [multiLoadsError]);

    React.useEffect(() => {
        if (loadGroupLists?.data?.records && !multiDevice) {
            const clone = JSON.parse(JSON.stringify(loadsPayload));
            delete clone['multiDevice'];
            clone['secondary'] = loadGroupLists?.data?.records;
            // Always keep the preview pane (widgetConfiguration.data) in sync with latest API result
            dispatch(setGaugeWidgetConfiguration(clone));

            // One-time reconciliation: update the "Selected" panel (RearrangeChannels reads from
            // the widget slice, not widgetConfiguration.data). Guarded by a ref so user
            // check/uncheck interactions are NOT overridden on subsequent query reruns.
            if (!hasReconciledRef.current) {
                hasReconciledRef.current = true;
                if (widgetId === 'load_group_trends') {
                    dispatch(setLoadGroupTrendsData(loadGroupLists.data.records));
                } else {
                    dispatch(setLoadGroupsData(loadGroupLists.data.records));
                }
            }
        }
    }, [loadGroupLists, loadsPayload, multiDevice]);
};
